<template>
  <div class="text-white">
    <header class="w-full p-6">
      <img
        src="/reeltrack-orange.svg"
        alt="ReelTrack"
        class="h-12"
        width="180"
        height="48"
      />
    </header>
    <main class="flex justify-center p-6 md:w-4/6 m-auto">
      <div class="hidden md:flex w-3/6">
        <div>
          <h2 class="text-2xl font-semibold">Are you a fan of TV series?</h2>
          <p class="text-lg mt-4 text-muted">
            Want to <span class="text-highlight">stay up-to-date</span> with the
            latest episodes?
          </p>
          <p class="text-lg font-semibold mt-4">
            Then <span class="text-highlight">ReelTrack</span> is for you!
          </p>
          <ul class="list-disc pl-6 my-6 space-y-2 text-muted">
            <li>Track your favourite TV series</li>
            <li>Get notified when new episodes are released</li>
            <li>View everything in your own timeline</li>
          </ul>
          <p class="text-lg mt-4 text-muted">
            So why wait? Start tracking your favourite series now and never miss
            an episode again!
          </p>
        </div>
        <div class="border-l-2 rounded-md ml-8 h-full border-primary-600"></div>
      </div>
      <div class="md:w-3/6 p-6">
        <h1 class="text-3xl font-semibold">Log in</h1>
        <div class="flex mt-6">
          <button
            v-for="provider in socialProviders"
            :key="provider.name"
            type="button"
            class="border border-primary-400 p-4 mr-2 rounded-md bg-primary-500 hover:bg-highlight"
            :aria-label="`Sign in with ${provider.name}`"
          >
            <img :src="provider.icon" alt="" width="20" height="20" />
          </button>
        </div>
        <form
          class="flex flex-col mt-8"
          autocomplete="on"
          @submit.prevent="handleLogin"
        >
          <InputField
            v-model="email"
            label="Email"
            type="email"
            name="email"
            inputmode="email"
            autocomplete="email"
            required
            placeholder="Enter your email"
          />
          <InputField
            v-model="password"
            label="Password"
            type="password"
            name="password"
            autocomplete="current-password"
            required
            class="mt-5"
            placeholder="Enter your password"
          />

          <div class="flex justify-between items-center mt-6">
            <Checkbox
              v-model="remember"
              label="Remember me"
              name="remember_me"
            />
            <router-link
              :to="{ path: '/forgot-password', query: forgotQuery }"
              class="text-highlight text-sm hover:underline"
            >
              Forgot password?
            </router-link>
          </div>

          <div>
            <Button class="mt-8 mb-3 w-full text-lg" :loading="loading">
              {{ loading ? "Signing in…" : "Sign in" }}
            </Button>
            <ErrorLabel :error="errorMessage" />
          </div>
        </form>
        <div>
          <p class="font-light mt-12 text-sm">
            <span class="text-muted">Don't have an account?</span>
            <router-link to="/register" class="text-highlight hover:underline">
              Sign up!
            </router-link>
          </p>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ViewName } from "@/router";
import ErrorLabel from "@/components/common/ErrorLabel.vue";
import { readRemember, readRememberedEmail } from "@/utils/tokenStorage";

export default {
  name: "Login",
  components: { ErrorLabel },

  data() {
    return {
      errorMessage: "",
      password: "",
      email: "",
      remember: false,
      loading: false,
      socialProviders: [
        { name: "Google", icon: "/social-google.png" },
        { name: "Facebook", icon: "/social-fb.png" },
        { name: "X.com", icon: "/social-x.png" },
      ],
    };
  },

  computed: {
    /** Carry a typed address over to the reset page. */
    forgotQuery() {
      const email = this.email.trim();
      return email ? { email } : {};
    },
  },

  mounted() {
    // A remembered sign-in pre-fills the address and re-ticks the box.
    this.remember = readRemember();
    this.email = this.$route.query.email ?? readRememberedEmail();
  },

  methods: {
    handleLogin() {
      if (this.loading === true) return;

      this.loading = true;
      this.errorMessage = "";

      this.$store
        .dispatch("login", {
          email: this.email,
          password: this.password,
          remember: this.remember,
        })
        .then(() => {
          // Honour the ?redirect= the auth guard added, when present.
          const redirect = this.$route.query.redirect;
          this.$router
            .push(redirect || { name: ViewName.Dashboard })
            .catch(() => undefined);
        })
        .catch((error) => {
          this.errorMessage = this.getAxiosErrorMessage(error);
        })
        .finally(() => (this.loading = false));
    },

    passwordPage() {
      // TODO - this.$router.push({ name: ViewName.UserPasswordReset });
    },

    getAxiosErrorMessage(error) {
      const status = error?.response?.status;

      if (status === 401) {
        return this.$i18n.t("auth.error.credentials").toString();
      } else if (status === 406) {
        // API returns 406 when the account email has not been verified.
        return this.$i18n.t("auth.error.unverified").toString();
      } else if (error) {
        return this.$i18n.t("auth.error.other").toString();
      }
      return "";
    },
  },
};
</script>
